import { Box, FormGroup } from "@mui/material";
import { Button, Card, Checkbox, FormLabel, Grid, Modal, ModalTitle, Typography, designTokens } from "@platform-ui/design-system";
import React, { useState, useEffect, useMemo } from "react"
import { Controller, useFormContext } from "react-hook-form";
import { DataGrid, GridRowSelectionModel, GridToolbar } from '@mui/x-data-grid';
import { Destination } from "@prequel/react";
import { BillingModelMetaData } from "../Constants";

interface BillingModelPropData {
    rows: BillingModelMetaData[];
    setDestinationField: (key: keyof Destination, value: string | string[] | boolean | undefined) => void;
    isEditMode?: boolean;
    initialProvisionedModelNames?: string[];
}

const getInitialSelectionForEditMode = (rows: BillingModelMetaData[], initialProvisionedModelNames?: string[]): { rowSelectionModel: GridRowSelectionModel; allCurrentAndFuture: boolean } => {
    if (!initialProvisionedModelNames || initialProvisionedModelNames.length === 0) {
        return { rowSelectionModel: [], allCurrentAndFuture: false };
    }
    if (initialProvisionedModelNames.length === 1 && initialProvisionedModelNames[0] === '*') {
        return { rowSelectionModel: rows.map((r) => r.id), allCurrentAndFuture: true };
    }
    const provisionedSet = new Set(initialProvisionedModelNames);
    const rowSelectionModel = rows.filter((r) => provisionedSet.has(r.objectName)).map((r) => r.id);
    return { rowSelectionModel, allCurrentAndFuture: false };
}

export const SelectModels = ({ rows, setDestinationField, isEditMode = false, initialProvisionedModelNames }: BillingModelPropData) => {
    const editModeInitial = useMemo(
        () => getInitialSelectionForEditMode(rows, initialProvisionedModelNames),
        [rows, initialProvisionedModelNames]
    );
    const [allCurrentAndFutureModelsSelected, setAllCurrentAndFutureModelsSelected] = useState(isEditMode ? editModeInitial.allCurrentAndFuture : false);
    const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>(isEditMode ? editModeInitial.rowSelectionModel : []);
    const [prevRowSelection, setPrevRowSelectionModel] = useState<GridRowSelectionModel>([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [paginationModel, setPaginationModel] = useState({
        pageSize: 10,
        page: 0,
    });

    useEffect(() => {
        if (isEditMode && initialProvisionedModelNames && rows.length > 0) {
            const { rowSelectionModel: sel, allCurrentAndFuture } = getInitialSelectionForEditMode(rows, initialProvisionedModelNames);
            setRowSelectionModel(sel);
            setAllCurrentAndFutureModelsSelected(allCurrentAndFuture);
        }
    }, [isEditMode, initialProvisionedModelNames, rows]);

    const {
        control,
        register,
        setValue,
        formState: { errors },
    } = useFormContext();

    const syncEnabledModels = (selectedModels: string[]) => {
        setDestinationField('enabled_models', selectedModels);
        setValue('enabled_models', selectedModels, { shouldValidate: true, shouldDirty: true });
    };

    const onClose = () => {
        if (isEditMode) {
            const { rowSelectionModel: sel, allCurrentAndFuture } = getInitialSelectionForEditMode(rows, initialProvisionedModelNames);
            setRowSelectionModel(sel);
            setAllCurrentAndFutureModelsSelected(allCurrentAndFuture);
        } else {
            setRowSelectionModel(prevRowSelection);
        }
        setModalOpen(false);
    };

    const handleModelSelection = () => {
        if (isEditMode) {
            const selectedModels = allCurrentAndFutureModelsSelected
                ? ['*']
                : rowSelectionModel.map((id) => rows.find((r) => r.id === id)?.objectName).filter(Boolean) as string[];
            syncEnabledModels(selectedModels);
        }
        setModalOpen(false);
    };

    return (
        <Controller
            name="connector-select-standard-objects"
            control={control}
            render={() => (
                <Box>
                    <Typography variant="title" body="Select Zuora Objects to receive Data" />
                    <FormLabel required>Billing Standard Objects</FormLabel>
                    <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
                        {rowSelectionModel?.length > 0 && (
                            <Typography sx={{ marginRight: '1%' }} variant="subtitle3" body={`${rowSelectionModel.length} of ${rows.length} objects selected`} />
                        )}
                        <Button
                            sx={{ marginBottom: '1%' }}
                            body={rowSelectionModel.length === 0 ? 'Select' : 'Reselect'}
                            dsOnClick={() => {
                                if (isEditMode && initialProvisionedModelNames && rows.length > 0) {
                                    const { rowSelectionModel: sel, allCurrentAndFuture } = getInitialSelectionForEditMode(rows, initialProvisionedModelNames);
                                    setRowSelectionModel(sel);
                                    setAllCurrentAndFutureModelsSelected(allCurrentAndFuture);
                                } else if (rowSelectionModel.length > 0) {
                                    setPrevRowSelectionModel(rowSelectionModel);
                                }
                                setModalOpen(true);
                            }}
                            variant={rowSelectionModel.length > 0 ? 'text' : 'contained'}
                        />
                    </Box>
                    {!isEditMode && (
                        <Box>
                            {!allCurrentAndFutureModelsSelected && rowSelectionModel.length === 0 && (
                                <Typography variant="caption" color={designTokens.colors.red500} body={errors?.enabled_models?.message as string ?? ''} />
                            )}
                        </Box>
                    )}
                    <Modal
                        {...(!isEditMode ? register('enabled_models', {
                            validate: () =>
                                !allCurrentAndFutureModelsSelected && rowSelectionModel.length === 0
                                    ? 'This is a required field'
                                    : true,
                        }) : {})}
                        maxWidth="sm"
                        fullWidth
                        open={modalOpen}
                        id="connector-select-models"
                        dsOnClose={onClose}
                        header={<ModalTitle dsOnClose={onClose} body="Select Billing Standard Objects" />}
                        body={
                            <DataGrid
                                disableRowSelectionOnClick
                                paginationModel={paginationModel}
                                isRowSelectable={() => !allCurrentAndFutureModelsSelected}
                                pageSizeOptions={[5, 10, 20]}
                                onPaginationModelChange={setPaginationModel}
                                sx={{
                                    '.MuiDataGrid-toolbarContainer': { display: 'block' },
                                    '.MuiTablePagination-selectLabel': { margin: '0px' },
                                    '.MuiTablePagination-displayedRows': { margin: '0px' },
                                    '.MuiDataGrid-filler': { display: 'none' },
                                }}
                                rows={rows}
                                disableColumnFilter
                                checkboxSelection
                                disableColumnSelector
                                disableDensitySelector
                                disableColumnMenu
                                slots={{
                                    toolbar: GridToolbar,
                                    noRowsOverlay: () => (
                                        <Box display="flex" alignItems="center" justifyContent="center" sx={{ height: "100%", padding: "1rem" }}>
                                            <Typography variant="body2" color="gray" body="No Objects found for this view" />
                                        </Box>
                                    ),
                                }}
                                slotProps={{
                                    toolbar: { showQuickFilter: true, csvOptions: { disableToolbarButton: true }, printOptions: { disableToolbarButton: true } },
                                    filterPanel: { disableAddFilterButton: true },
                                }}
                                onRowSelectionModelChange={(newRowSelectionModel) => {
                                    setRowSelectionModel(newRowSelectionModel);
                                    syncEnabledModels(
                                        rows
                                            .filter((row) => newRowSelectionModel.indexOf(row.id) !== -1)
                                            .map((row) => row.objectName)
                                    );
                                }}
                                rowSelectionModel={rowSelectionModel}
                                columns={[{ field: 'id', headerName: 'Number' }, { field: 'objectName', headerName: 'Object Name', sortable: true, width: 200 }]}
                            />
                        }
                        footer={
                            <Card id="select-future-objects" body={
                                <FormGroup>
                                    <Checkbox
                                        label={<Typography variant="title" body="Select all current and future objects" />}
                                        dsOnChange={(e) => {
                                            setAllCurrentAndFutureModelsSelected(e.target.checked);
                                            if (e.target.checked) {
                                                setRowSelectionModel(Array.from({ length: rows.length }, (_, i) => i + 1));
                                                syncEnabledModels(["*"]);
                                            } else {
                                                setRowSelectionModel([]);
                                                syncEnabledModels([]);
                                            }
                                        }}
                                        checked={allCurrentAndFutureModelsSelected}
                                    />
                                    <Typography variant="caption" color={designTokens.colors.coolGray300} body="Objects added in the future will be synced automatically" />
                                    <Grid container direction="row" justify="flex-end">
                                        <Grid item>
                                            <Button dsOnClick={onClose} body="Cancel" variant="tertiary" size="medium" />
                                        </Grid>
                                        <Grid item>
                                            <Button dsOnClick={isEditMode ? handleModelSelection : () => setModalOpen(false)} body="Done" size="medium" />
                                        </Grid>
                                    </Grid>
                                </FormGroup>
                            }>
                            </Card>
                        }
                    />
                </Box>
            )}
        />
    );
};